import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Car, Zap, Gauge, Users, Weight, Battery, Star, ChevronLeft } from 'lucide-react';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import AdSlot from '../components/AdSlot';
import ShareButtons from '../components/ShareButtons';
import NotFoundPage from './NotFoundPage';
import RichContent, { stripHtml } from '../components/RichContent';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function VehicleDetailPage() {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/vehicles/${vehicleId}`).then(r => setV(r.data.vehicle)).catch(() => setV(null)).finally(() => setLoading(false));
  }, [vehicleId]);

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-16 text-center text-slate-400">Yükleniyor…</div>;
  if (!v) return <NotFoundPage title="Araç bulunamadı" message="Aradığınız araç kataloğumuzdan kaldırılmış ya da adresi hatalı olabilir." />;

  // Build description from actual spec data only
  const parts = [`${v.brand} ${v.model}`];
  if (v.category) parts.push(`${v.category} kategorisi`);
  if (v.max_speed) parts.push(`maks. ${v.max_speed} km/s`);
  if (v.range_km) parts.push(`${v.range_km} km menzil`);
  if (v.power_kw) parts.push(`${v.power_kw} kW motor`);
  if (v.battery_kwh) parts.push(`${v.battery_kwh} kWh batarya`);
  const metaDesc = `${v.brand} ${v.model} teknik özellikleri: ${parts.slice(1).join(', ') || 'MiniOto kataloğunda detayları incele'}.`.slice(0, 160);
  // Vehicle JSON-LD - only include fields that actually exist (no fabricated price/rating)
  const specs = [];
  if (v.max_speed) specs.push({ '@type': 'PropertyValue', name: 'Azami hız', value: v.max_speed, unitCode: 'KMH' });
  if (v.range_km) specs.push({ '@type': 'PropertyValue', name: 'Menzil', value: v.range_km, unitCode: 'KMT' });
  if (v.power_kw) specs.push({ '@type': 'PropertyValue', name: 'Motor gücü', value: v.power_kw, unitCode: 'KWT' });
  if (v.battery_kwh) specs.push({ '@type': 'PropertyValue', name: 'Batarya kapasitesi', value: v.battery_kwh, unitCode: 'KWH' });
  if (v.weight_kg) specs.push({ '@type': 'PropertyValue', name: 'Ağırlık', value: v.weight_kg, unitCode: 'KGM' });
  if (v.seats) specs.push({ '@type': 'PropertyValue', name: 'Koltuk sayısı', value: v.seats });
  if (v.license_required) specs.push({ '@type': 'PropertyValue', name: 'Ehliyet', value: v.license_required });

  const vehicleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Vehicle',
    name: `${v.brand} ${v.model}`,
    brand: { '@type': 'Brand', name: v.brand },
    model: v.model,
    vehicleConfiguration: v.category,
    ...(v.image_url ? { image: v.image_url.startsWith('http') ? v.image_url : `${process.env.REACT_APP_BACKEND_URL}/api/files/${v.image_url}` } : {}),
    ...(v.description ? { description: stripHtml(v.description, 500) } : {}),
    ...(specs.length ? { additionalProperty: specs } : {}),
  };
  // Only emit aggregateRating if there are REAL ratings
  if (v.rating_count && v.rating_count > 0 && v.rating_avg) {
    vehicleSchema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: v.rating_avg,
      ratingCount: v.rating_count,
      bestRating: 5,
      worstRating: 1,
    };
  }

  const imgUrl = v.image_url && (v.image_url.startsWith('http') ? v.image_url : `${API}/files/${v.image_url}`);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO
        title={`${v.brand} ${v.model}${v.category ? ' ' + v.category : ''}`}
        description={metaDesc}
        image={imgUrl}
        type="product"
        canonicalPath={`/arac/${v._id}`}
        noindex={!!v.noindex}
        jsonLd={vehicleSchema}
      />
      <Breadcrumbs items={[
        { name: 'Araçlar', href: '/araclar' },
        ...(v.category ? [{ name: v.category, href: `/araclar?kategori=${v.category}` }] : []),
        { name: v.brand, href: `/araclar?marka=${encodeURIComponent(v.brand)}` },
        { name: v.model },
      ]} />
      <AdSlot placement="catalog_top" className="mb-6" />

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="grid md:grid-cols-2 gap-0">
          <div className="aspect-video md:aspect-auto bg-slate-100">
            {imgUrl ? (
              <img src={imgUrl} alt={`${v.brand} ${v.model}`} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><Car className="w-20 h-20 text-slate-300" /></div>
            )}
          </div>
          <div className="p-6 md:p-8">
            {v.category && <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full mb-3 ${v.category === 'L6e' ? 'bg-emerald-50 text-emerald-700' : 'bg-cyan-50 text-cyan-700'}`}>{v.category}</span>}
            <h1 className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{v.brand} {v.model}</h1>
            {v.description && <div className="mt-3"><RichContent html={v.description} /></div>}
            {v.rating_count > 0 && (
              <div className="flex items-center gap-1 mt-4" data-testid="vehicle-rating">
                {[1,2,3,4,5].map(n => <Star key={n} className={`w-4 h-4 ${n <= Math.round(v.rating_avg || 0) ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />)}
                <span className="text-sm text-slate-500 ml-2 font-medium">{v.rating_avg} <span className="text-slate-400 font-normal">({v.rating_count} oy)</span></span>
              </div>
            )}
            <div className="mt-5 pt-5 border-t border-slate-100 flex items-center gap-3 flex-wrap">
              <span className="text-xs font-semibold text-slate-500">Paylaş:</span>
              <ShareButtons title={`${v.brand} ${v.model}`} text={`${v.brand} ${v.model} özelliklerini MiniOto'da incele`} />
            </div>
          </div>
        </div>

        <div className="border-t border-slate-100 p-6 md:p-8 grid sm:grid-cols-2 md:grid-cols-3 gap-4">
          <Spec icon={Gauge} label="Azami hız" value={v.max_speed ? `${v.max_speed} km/s` : null} />
          <Spec icon={Battery} label="Menzil" value={v.range_km ? `${v.range_km} km` : null} />
          <Spec icon={Zap} label="Motor gücü" value={v.power_kw ? `${v.power_kw} kW` : null} />
          <Spec icon={Battery} label="Batarya" value={v.battery_kwh ? `${v.battery_kwh} kWh` : null} />
          <Spec icon={Users} label="Koltuk" value={v.seats || null} />
          <Spec icon={Weight} label="Ağırlık" value={v.weight_kg ? `${v.weight_kg} kg` : null} />
        </div>
      </div>

      <div className="mt-6">
        <Link to="/araclar" className="inline-flex items-center text-emerald-700 text-sm font-semibold hover:text-emerald-800" data-testid="back-to-catalog">
          <ChevronLeft className="w-4 h-4 mr-1" /> Tüm araçlar
        </Link>
      </div>
    </div>
  );
}

function Spec({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-sm font-semibold text-slate-800">{value}</div>
      </div>
    </div>
  );
}
